import { cn } from "@/utils/cn";

type Props = {
  data: unknown;
  className?: string;
};

/**
 * Zero-dependency, syntax-aware JSON pretty-printer. Produces a single
 * <pre> with small inline spans so operators can read claims without
 * the overhead of a full syntax-highlighter dependency.
 */
export function JsonViewer({ data, className }: Props) {
  const pretty = safeStringify(data, 2);
  return (
    <pre
      className={cn(
        "max-h-96 overflow-auto rounded border border-slate-200 bg-slate-50 p-3 font-mono text-[11.5px] leading-relaxed text-slate-800",
        className,
      )}
    >
      {colorize(pretty)}
    </pre>
  );
}

function safeStringify(value: unknown, indent: number): string {
  try {
    return JSON.stringify(value, null, indent);
  } catch {
    return String(value);
  }
}

const TOKEN_REGEX =
  /("(?:\\.|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function colorize(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(TOKEN_REGEX)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(text.slice(last, idx));
    const raw = m[0];
    parts.push(
      <span key={i++} className={classFor(raw)}>
        {raw}
      </span>,
    );
    last = idx + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function classFor(token: string): string {
  if (token.endsWith(":")) return "text-brand-700";
  if (token.startsWith('"')) return "text-emerald-700";
  if (token === "true" || token === "false" || token === "null")
    return "text-violet-700";
  return "text-amber-700";
}
